import { useState } from 'react'
import { formatDate } from '../../lib/format'
import { ORIGIN_WORDS, PART_WORDS, provenanceWords, reasonCodeWords, reasonLine, splitAt } from '../../lib/workbench'
import type { DraftOverride, DraftPart, DraftVersionView, LintFlag, Provenance, VoiceTrait } from '../../types/workbench'
import { CalloutNote, LintChip } from './WorkbenchParts'

// The email as a hierarchy of data (REQ-100). Every part is drawn as a document line with, beneath the surface,
// where it came from (the model or the rep's own words), why it is here (the reason code) and what it stands on
// (its sources). Four layers can be lifted: OVERLAYS colour each part by its origin and tag its reason; COMMENTS
// hang a numbered mark on each line that opens, on hover or a click, a bubble pointing at the text — the callouts,
// the lint and the why, the way a reviewer's comments sit on a document (owner, 2026-09-25: not a gutter taking a
// third of the width); SOURCES adds every fact with its date to the bubble; OVERRIDES lists every wording the rep
// typed over the model's, with a revert. Presentation only; nothing sends.

export type Layer = 'overlays' | 'comments' | 'sources' | 'overrides'
export const LAYERS: { id: Layer; label: string }[] = [
  { id: 'overlays', label: 'overlays' }, { id: 'comments', label: 'comments' }, { id: 'sources', label: 'sources' }, { id: 'overrides', label: 'overrides' },
]
export const DEFAULT_LAYERS: Layer[] = ['overlays', 'comments']

/** The layer toggles. */
export function LayerToggles({ on, onToggle }: { on: Layer[]; onToggle: (l: Layer) => void }) {
  return (
    <div className="layer-toggles" role="group" aria-label="What to show beneath the email">
      {LAYERS.map((l) => (
        <button key={l.id} type="button" className={`chip-filter ${on.includes(l.id) ? 'on' : ''}`} aria-pressed={on.includes(l.id)} onClick={() => onToggle(l.id)}>{l.label}</button>
      ))}
    </div>
  )
}

/** The band over a part: its origin in words and its reason as a tag. */
export function OverlayBand({ part }: { part: DraftPart }) {
  return (
    <span className={`overlay-band origin-${part.origin}`}>
      <span className="origin">{ORIGIN_WORDS[part.origin] ?? part.origin}</span>
      <span className="reason-tag">{reasonCodeWords(part.part, part.reasonCode)}</span>
    </span>
  )
}

/** The sources a part stands on, each with where and when. */
export function SourceList({ sources }: { sources: Provenance[] }) {
  if (sources.length === 0) return <span className="muted small">stands on nothing on file — your own words</span>
  return (
    <ul className="plain source-list">
      {sources.map((s, i) => <li key={i} className="small">{provenanceWords(s, formatDate)}</li>)}
    </ul>
  )
}

/** How many notes a line carries: its why, its callouts, its lint, and (when lifted) its sources. */
export function commentCount(part: DraftPart, flags: { flag: LintFlag }[], showSources: boolean): number {
  return (reasonLine(part.reasonCode) ? 1 : 0) + part.callouts.length + flags.length + (showSources ? 1 : 0)
}

/** The reviewer's mark on a line: a numbered dot at its edge; hover or click it and a bubble opens, pointing at the
 *  text, with why the part is here, its callouts, its lint and (when lifted) its sources. A line with nothing to say
 *  carries no mark. A lint mark is coloured as a warning so the eye finds it first. */
export function CommentMark({ part, flags, showSources, activeFlag, onFlag, showOrigin = false }: {
  part: DraftPart; flags: { flag: LintFlag; span?: [number, number] }[]; showSources: boolean; activeFlag?: LintFlag | null; onFlag?: (f: LintFlag) => void
  /** the overlays layer: the bubble's head also says who wrote the part and its reason tag */
  showOrigin?: boolean
}) {
  const [pinned, setPinned] = useState(false)
  const n = commentCount(part, flags, showSources)
  if (n === 0) return null
  const why = reasonLine(part.reasonCode)
  const worst = flags.some((f) => f.flag.kind === 'caution' || f.flag.kind === 'stale-date') ? 'danger' : flags.length > 0 ? 'warn' : ''
  return (
    <span className={`comment-anchor ${pinned ? 'pinned' : ''}`} onMouseLeave={() => setPinned(false)}>
      <button type="button" className={`comment-dot ${worst}`} aria-expanded={pinned} aria-label={`${n} ${n === 1 ? 'note' : 'notes'} on ${PART_WORDS[part.part] ?? part.part}`}
        onClick={() => setPinned((p) => !p)} title={`${n} ${n === 1 ? 'note' : 'notes'} — hover or click`}>{n}</button>
      <div className="comment-bubble" role="note">
        <span className="comment-part muted small">{PART_WORDS[part.part] ?? part.part}{showOrigin && <> · <OverlayBand part={part} /></>}</span>
        {why && <p className="comment-why small">{why}</p>}
        {part.callouts.length > 0 && <ul className="plain callouts">{part.callouts.map((c, i) => <CalloutNote key={i} c={c} />)}</ul>}
        {flags.length > 0 && <div className="comment-lint">{flags.map((f, i) => <LintChip key={i} flag={f.flag} active={activeFlag === f.flag} onClick={onFlag ? () => onFlag(f.flag) : undefined} />)}</div>}
        {showSources && <div className="comment-sources"><span className="muted small">stands on</span><SourceList sources={part.sources ?? []} /></div>}
      </div>
    </span>
  )
}

/** The text of a part with a lint span marked; the mark says what the lint found on hover. */
export function PartText({ part, span, note }: { part: DraftPart; span?: [number, number]; note?: string }) {
  const [a, b, c] = splitAt(part.text, span)
  // a span, not a paragraph: the marker follows the words on the same line
  return <span className="part-text">{a}{b && <mark title={note}>{b}</mark>}{c}</span>
}

/** The radial (REQ-106): one axis per trait, the rep's profile as the filled shape, the draft's measured register as the
 *  outline where the verifier measured it. Pure SVG; every number is the rep's own. */
export function VoiceRadial({ traits, size = 150 }: { traits: VoiceTrait[]; size?: number }) {
  const n = traits.length
  if (n < 3) return null
  const c = size / 2, r = c - 22
  const pt = (i: number, v: number) => { const a = -Math.PI / 2 + (2 * Math.PI * i) / n; return [c + Math.cos(a) * r * v, c + Math.sin(a) * r * v] as const }
  const path = (get: (t: VoiceTrait) => number | null | undefined) => {
    const pts = traits.map((t, i) => { const v = get(t); return v === null || v === undefined ? null : pt(i, Math.max(0.04, Math.min(1, v))) })
    if (pts.some((p) => p === null)) return null
    return pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p![0].toFixed(1)},${p![1].toFixed(1)}`).join(' ') + ' Z'
  }
  // the draft's outline departs from the profile only on the axes the verifier measured; the rest sit on the profile
  const measured = traits.some((t) => t.draft !== null && t.draft !== undefined)
  const profile = path((t) => t.value), draft = measured ? path((t) => t.draft ?? t.value) : null
  return (
    <svg className="voice-radial" viewBox={`0 0 ${size} ${size}`} width={size} height={size} role="img" aria-label={`Your voice on ${n} traits`}>
      {[0.25, 0.5, 0.75, 1].map((k) => <circle key={k} className="radial-ring" cx={c} cy={c} r={r * k} />)}
      {traits.map((t, i) => { const [x, y] = pt(i, 1); return <line key={t.key} className="radial-axis" x1={c} y1={c} x2={x} y2={y} /> })}
      {profile && <path className="radial-profile" d={profile} />}
      {draft && <path className="radial-draft" d={draft} />}
      {traits.map((t, i) => { const [x, y] = pt(i, 1.16); return <text key={t.key} className="radial-label" x={x} y={y} textAnchor="middle" dominantBaseline="central"><title>{t.note}</title>{t.label}</text> })}
    </svg>
  )
}

/** The voice marker beside a part's words (REQ-106): a small radial glyph; hover or click and a bubble opens with the
 *  radial, why the part reads as it does, the scenario, and the tones on offer — choose one and the conversation
 *  revises the part with that instruction, which is a new version. */
export function VoiceMark({ part, onTone, busy = false }: { part: DraftPart; onTone?: (instruction: string) => void; busy?: boolean }) {
  const [pinned, setPinned] = useState(false)
  const v = part.voice
  if (!v) return null
  return (
    <span className={`comment-anchor voice-anchor ${pinned ? 'pinned' : ''}`} onMouseLeave={() => setPinned(false)}>
      <button type="button" className="voice-dot" aria-expanded={pinned} aria-label={`The voice behind ${PART_WORDS[part.part] ?? part.part}`} title="Why it reads like you — hover or click"
        onClick={() => setPinned((p) => !p)}>
        <svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true"><path d="M10 2 L16.9 6 L16.9 14 L10 18 L3.1 14 L3.1 6 Z" fill="none" stroke="currentColor" strokeWidth="1.4" /><path d="M10 6 L13.5 8 L13 12.5 L10 14 L6.5 12 L7 8 Z" fill="currentColor" opacity="0.55" /></svg>
      </button>
      <div className="comment-bubble voice-bubble" role="note">
        <span className="comment-part muted small">The voice · {PART_WORDS[part.part] ?? part.part} · {v.scenario === 'all' ? 'your mail overall' : <>your <strong>{v.scenario}</strong> mail</>}</span>
        <div className="voice-body">
          <VoiceRadial traits={v.traits} />
          <div className="voice-text">
            <p className="comment-why small">{v.why}</p>
            <ul className="plain voice-traits small">{v.traits.map((t) => <li key={t.key}><span className="muted">{t.label}</span> {t.note}</li>)}</ul>
          </div>
        </div>
        <div className="voice-tones" role="group" aria-label="A different tone for this part">
          <span className="muted small">Try it</span>
          {v.tones.map((t) => <button key={t.key} type="button" className="chip-filter tone" disabled={busy || !onTone} onClick={() => onTone?.(t.instruction)} title={t.instruction}>{t.label}</button>)}
        </div>
        <p className="muted small voice-note">A tone change revises this part through the conversation and is kept as a version.</p>
      </div>
    </span>
  )
}

const VERSION_WORDS: Record<string, string> = { drafted: 'as first drafted', revised: 'revised', struck: 'phrase struck', restored: 'put back' }

/** The draft's versions (REQ-104): one pill per state, the current one marked; press one to step to it. Nothing until
 *  the conversation has made a sweeping change. */
export function VersionStrip({ versions, onRestore, busy = false }: { versions: DraftVersionView[]; onRestore?: (seq: number) => void; busy?: boolean }) {
  if (versions.length === 0) return null
  const current = versions.findIndex((v) => v.current)
  const step = (to: number) => { const v = versions[to]; if (v && onRestore && !v.current) onRestore(v.seq) }
  return (
    <div className="version-strip" role="group" aria-label="Versions of the draft">
      <button type="button" className="link-button version-step" onClick={() => step(current - 1)} disabled={busy || !onRestore || current <= 0} title="The version before this one" aria-label="Back one version">‹</button>
      {versions.map((v) => (
        <button key={v.seq} type="button" className={`version-pill kind-${v.kind} ${v.current ? 'current' : ''}`} aria-pressed={v.current} disabled={busy || !onRestore || v.current}
          onClick={() => onRestore?.(v.seq)} title={`v${v.seq} · ${VERSION_WORDS[v.kind] ?? v.kind} · ${v.why} · ${formatDate(v.at)}${v.current ? ' · the draft stands on this' : ' · press to put it back'}`}>
          v{v.seq}<span className="version-why">{v.kind === 'drafted' ? 'first draft' : v.why.length > 28 ? v.why.slice(0, 27) + '…' : v.why}</span>
        </button>
      ))}
      <button type="button" className="link-button version-step" onClick={() => step(current + 1)} disabled={busy || !onRestore || current < 0 || current >= versions.length - 1} title="The version after this one" aria-label="Forward one version">›</button>
    </div>
  )
}

/** Every wording the rep typed over the model's, newest last, each revertable to the model's words. */
export function OverridesPanel({ overrides, onRevert, busy = false }: { overrides: DraftOverride[]; onRevert?: (o: DraftOverride) => void; busy?: boolean }) {
  if (overrides.length === 0) return <p className="muted small">No wording of yours yet — the whole draft is as drafted for you. Type into any part to change it; the change is kept here.</p>
  return (
    <ol className="plain overrides">
      {overrides.map((o, i) => (
        <li key={i} className="override">
          <span className="muted small">{PART_WORDS[o.part] ?? o.part} · your words · {formatDate(o.at)}</span>
          <span className="before small"><s>{o.before}</s></span>
          <span className="after">{o.after}</span>
          {onRevert && <button type="button" className="button small" onClick={() => onRevert(o)} disabled={busy} title="Put the model's words back for this part">revert</button>}
        </li>
      ))}
    </ol>
  )
}
